<template>
    <div :class="portalContentAndDesignStore.getPageContainerClasses">
        <div>
            <div id="partner-login">

                <regular-form v-if="!portalContentAndDesignStore.loginPageSettings.is_otp_login" />
                <otp-ini-form v-else />

                <honeypot-fields />

                <!-- Turnstile Widget -->
                <div v-if="turnstileEnabled" ref="turnstileContainer" class="my-4"></div>

                <editable-btn
                    :prevent-double-click="5000"
                    size="huge"
                    class="w-full"
                    :type="buttonType"
                    :corners="portalContentAndDesignStore.getPublicPageButtonCorners"
                    v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_login"
                    :loading="portalContentAndDesignStore.loggingInProgress"
                    :disabled="portalContentAndDesignStore.loggingInProgress"
                    @click="submitLogin"
                >
                    {{ portalContentAndDesignStore.translationSettings.portal_translation_login_login }}
                </editable-btn>

                <!-- Social Auth -->
                <template v-if="portalContentAndDesignStore.portalLayoutSettings.portal_affiliate_enable_partners_to_login_signup_via_google">
                    <div class="flex flex-row items-center justify-center my-4">
                        <div class="w-full">
                            <hr>
                        </div>
                        <div class="px-4">
                            <editable-text-field
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_or"
                                class="block text-sm opacity-80 mb-1.5"
                                :class="portalContentAndDesignStore.getPageContentTextColorClass"
                                text-type="span"
                            >
                            </editable-text-field>
                        </div>
                        <div class="w-full">
                            <hr>
                        </div>
                    </div>

                    <editable-social-auth-btn
                        social-provider="google"
                        :social-submit-url="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getSocialAuthLoginRedirectRoute('google')"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_login_with_google"
                    />
                </template>
            </div>
        </div>
        <!-- Register section -->
        <div v-if="!portalContentAndDesignStore.portalLayoutSettings.referral_portal_portal_disable_registrations_via_portal"
             :class="[
                'flex space-x-1 pt-6 text-sm justify-center',
                 portalContentAndDesignStore.getPageContentTextColorClass
             ]">

            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_not_registered"
                text-type="span"
            />

            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_create_account"
                text-type="a"
                :href="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getRegisterRoute"
                :link-target="portalContentAndDesignStore.isExternalRegisterRoute ? '_blank' : '_self'"
                :class="[
                    'text-opacity-100 hover:underline',
                    portalContentAndDesignStore.getLinkColorClass
                ]"
            />
        </div>

        <!-- Sub-account login section -->
        <div v-if="portalContentAndDesignStore.subAccountsEnabled"
             :class="[
                'flex space-x-1 pt-4 text-sm justify-center',
                 portalContentAndDesignStore.getPageContentTextColorClass
             ]">

            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_have_sub_account"
                text-type="span"
            />

            <editable-text-field
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_login_sub_account_login_here"
                text-type="a"
                :href="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getSubAccountLoginRoute"
                :class="[
                    'text-opacity-100 hover:underline',
                    portalContentAndDesignStore.getLinkColorClass
                ]"
            />
        </div>
    </div>
</template>

<script setup>
import {computed, onMounted, nextTick, ref, watch} from "vue";
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import RegularForm from "./Partial/Affiliate/RegularForm.vue";
import OtpIniForm from "./Partial/Affiliate/OtpIniForm.vue";
import EditableBtn from "../../../portal-editable-fields/EditableBtn.vue";
import EditableTextField from "../../../portal-editable-fields/EditableTextField.vue";
import EditableSocialAuthBtn from "../../../portal-editable-fields/EditableSocialAuthBtn.vue";
import HoneypotFields from "../../HoneypotFields.vue";
import {useLogin} from "../../../../../composables/partners/useLogin";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const turnstileContainer = ref(null);
const turnstileToken = ref(null);
// Set when Turnstile can't issue a token (errors, e.g. domain not allowlisted,
// or never loads) so auto-submit doesn't wait for it forever.
const turnstileUnavailable = ref(false);
let turnstileWidgetId = null;
let autoSubmitFallbackTimer = null;

const { submitLogin, turnstileEnabled } = useLogin(turnstileToken);

const buttonType = computed(() => {
    return portalContentAndDesignStore.getPageContentButtonColorClass
});

const initTurnstile = () => {
    const settings = portalContentAndDesignStore.turnstileSettings;
    if (!settings?.turnstile_enabled || !turnstileContainer.value) return;

    const renderWidget = () => {
        if (typeof window.turnstile === 'undefined') {
            setTimeout(renderWidget, 100);
            return;
        }
        if (turnstileWidgetId !== null) return;

        turnstileWidgetId = window.turnstile.render(turnstileContainer.value, {
            sitekey: settings.turnstile_site_key,
            callback: (token) => { turnstileToken.value = token; },
            'expired-callback': () => { turnstileToken.value = null; },
            'error-callback': () => { turnstileToken.value = null; turnstileUnavailable.value = true; },
        });
    };

    renderWidget();
};

let hasAutoSubmitted = false;

// Safety net: if Turnstile is enabled but never issues a token and never fires its
// error callback (e.g. the script is blocked from loading), proceed anyway after a
// short grace period. Armed only once we're actually waiting on a token.
const armTurnstileFallback = () => {
    if (autoSubmitFallbackTimer) return;
    autoSubmitFallbackTimer = setTimeout(() => {
        turnstileUnavailable.value = true;
    }, 5000);
};

// Auto-submit the first login step when requested via URL (?email=..&autosubmit=1).
// Only applies to OTP login (there is no password to submit otherwise) and runs once.
// When Turnstile is enabled we prefer to submit with a token, but we don't wait for
// it indefinitely: if the challenge errors (e.g. the custom domain isn't in the
// sitekey's allowlist, error 110200) or never resolves, we submit without one.
// This matches manual login, which only attaches the token when present, and the
// backend treats a missing token as verified.
const maybeAutoSubmit = () => {
    if (hasAutoSubmitted || !portalContentAndDesignStore.loginAutoSubmit) return;
    if (portalContentAndDesignStore.isPreview) return;
    if (!portalContentAndDesignStore.loginPageSettings.is_otp_login) return;
    if (!portalContentAndDesignStore.loginFormData.email) return;
    if (portalContentAndDesignStore.loginFormData.otpIdentifier) return;
    if (turnstileEnabled.value && !turnstileToken.value && !turnstileUnavailable.value) {
        armTurnstileFallback();
        return;
    }

    hasAutoSubmitted = true;
    if (autoSubmitFallbackTimer) {
        clearTimeout(autoSubmitFallbackTimer);
        autoSubmitFallbackTimer = null;
    }
    submitLogin();
};

onMounted(() => {
    nextTick(() => {
        initTurnstile();
        maybeAutoSubmit();
    });
});

// Re-evaluate auto-submit whenever the relevant state changes: the store is
// populated by the (parent) initializer's onMounted, which may run after this
// component mounts, and when Turnstile is disabled there is no token to wait on.
watch(
    [
        turnstileToken,
        turnstileUnavailable,
        () => portalContentAndDesignStore.loginAutoSubmit,
        () => portalContentAndDesignStore.loginFormData.email,
        () => portalContentAndDesignStore.loginPageSettings.is_otp_login,
    ],
    () => maybeAutoSubmit()
);
</script>
